/**
 * Simulator implementation behind the public package facade.
 * createSim(airport, options) → Sim
 *
 * Fixed 0.25 s integration steps, scaled by timeScale. Hosts call step(wallDt) from their
 * own loop and read events/ticks. The sim owns all rules; views only draw what it publishes.
 */
import { approachLabel, bearingDeg, defaultApproach, distanceM, findRunwayEnd, NM_TO_M, realismOptions, runwayConfig, toMagneticDeg, toTrueDeg, type AircraftState, type AirportData, type EnvState, type RealismLevel, type RealismOptions, type RunwayConfig } from '@atc/core';
import { isOnGroundPhase, isRemovedPhase, runwayPairName, setPhase, stepAircraft, type Aircraft, type AircraftEvent, type StepContext, type Wind } from './aircraft';
import { PILOT_HELP } from './command-catalog';
import { HELP_TEXT, parseCommand, type Instruction, type SystemCommand } from './commands';
import { altitudeSpeech, altitudeText, atcPhrase, callsignPhrase, fixSpeech, pilotPhrase, runwaySpeech, type Phrase, type RadioEffect, type RadioKind, type RadioMessage } from './comms';
import { checkInstruction, executeInstruction, instructionAxis, phraseInstruction, recheckInstruction, rejoinIndex, unablePhrase, type InstructionPlan } from './pilot';
import { acknowledgeRequest, dueStage, isDepartureRequest, newRequest, NEEDS, patiencePhrase, requestOutcome, STANDBY_LIMIT, STANDBY_S, type PatienceStage, type RequestKind } from './patience';
import { performanceFor } from './performance';
import { deriveSeed, Rng } from './rng';
import { accentFor, airportAccent, callsignFormFor, radioStyleFor } from './airlines';
import { plannedPath } from './planned-path';
import { burnFuel, FUEL_CHECK_EVERY_S, fuelMarginMin, fuelStateFor, initialFuel, minutesToLand } from './fuel';
import { applyEmergencyPerformance, EmergencyGenerator, EMERGENCY_RATES, NATURE_CODE, NATURE_TEXT, personsFor, startEmergency, TIGHT_FUEL_CHANCE, type EmergencyNature, type EmergencyRate } from './emergency';
import { computeScore, newStats, statsText, type Stats } from './scoring';
import { findConflicts, predictConflicts } from './separation';
import { createAircraft, Spawner, type AircraftSpec } from './spawner';
import { occupiedEnd, occupiedRunways, runwayUnable, runwaysIntersect } from './runways';
import { validateClearance } from './clearance';
import { clarificationFor, instructionSummary, pilotAnswer, QUERY_SUBJECTS, validateTransmission, type PilotMemory } from './pilot-brain';
import { renderParts, type AllowedValues } from './brain/phrase';
import { createRulesBrain, flavourOf } from './brain/rules-brain';
import { CREW_STYLES, variantOf } from './phraseology';
import { validateDecision } from './brain/validate';
import { situationFor } from './brain/situation';
import { deadlineFor, MAX_PENDING_PER_AIRCRAFT, type BrainLogEntry, type BrainMiss } from './brain/host';
import { isAsyncBrain, type AnyPilotBrain, type PilotDecision, type PilotIntent, type PilotTrigger } from './brain/types';
import { DEFAULT_CHECKIN_SPACING_S, FIRST_CHECKIN_DELAY_S, OPENING_QUIET_S, STEP_S, type AircraftContext, type CommandResult, type DebriefEvent, type Sim, type SimEvents, type SimOptions } from './sim-contracts';
import { garbleReadback } from './readback';
import { attachDebriefTimeline } from './debrief';
import { createOperations } from './operations';
import { detectImpacts, type Position } from './collision';

/**
 * ASSUMPTION: an uncleared arrival breaks off 4 nm from the airport reference point — close enough
 * that a controller who meant to clear it has had every chance, far enough to turn before the
 * runway. It does not do so again for five minutes, so a player who vectors it back gets time.
 */
const NEAR_FIELD_NM = 4;
const NEAR_FIELD_REPEAT_S = 300;
/** Clearances only the runway's owner gives; in an Academy approach position that is the tower colleague. */
const RUNWAY_CLEARANCES: readonly Instruction['kind'][] = ['land', 'takeoff', 'lineUp'];
import { towerRunwayRisk } from './tower-profile';
import { createRadioChannel } from './radio-channel';
export * from './operations';
export * from './tower-profile';

export * from './aircraft';
export * from './commands';
export * from './comms';
export * from './performance';
export * from './pilot';
export * from './scoring';
export * from './separation';
export * from './spawner';
export * from './airlines';
export * from './runways';
export * from './clearance';
export * from './pilot-brain';
export * from './command-catalog';
export * from './patience';
export * from './planned-path';
export * from './phraseology';
export * from './fuel';
export * from './emergency';
export * from './brain/phrase';
export * from './brain/types';
export * from './brain/rules-brain';
export * from './brain/validate';
export * from './brain/situation';
export * from './brain/host';
export { Rng, deriveSeed } from './rng';
export * from './sim-contracts';
export * from './collision';

export function createSim(airport: AirportData, options: SimOptions = {}): Sim {
  const rng = new Rng(options.seed ?? 1);
  let realism = realismOptions(options.realism ?? 'low');
  const scopeRadiusNm = options.scopeRadiusNm ?? 35;
  const maxAircraft = options.maxAircraft ?? 30;
  const spawner = new Spawner(airport, rng, {
    scheduled: options.position === 'tower',
    scopeRadiusNm, arrivalsPerHour: options.arrivalsPerHour ?? 24, departuresPerHour: options.departuresPerHour ?? 18,
    maxQueuePerRunway: 3, shortCallsigns: options.shortCallsigns ?? true,
    activeRunways: options.activeRunways,
    // Only when the session opens with traffic; scripted scenarios start empty and spawn on their own terms.
    ...(options.initialTraffic !== false ? { openingQuietS: OPENING_QUIET_S } : {}),
  });
  let pilotInitiative = options.pilotInitiative ?? 'normal';
  const patience = options.pilotPatience !== false;
  const rulesBrain = createRulesBrain();
  let brain: AnyPilotBrain = options.brain ?? rulesBrain;
  let brainRequestId = 0;
  /** Asked, not yet answered: the rules answer waits here with the deadline. */
  const brainPending = new Map<number, { ac: Aircraft; trigger: PilotTrigger; allowed: AllowedValues; fallback: PilotDecision; askedAtS: number; deadlineS: number }>();
  const brainInbox: { id: number; decision: unknown; usage?: Record<string, number> }[] = [];
  const brainRecord: BrainLogEntry[] = [];
  const fuelOn = options.fuel !== false;
  // Their own streams, so neither fuel nor emergencies shift the traffic of a seeded run.
  const fuelRng = new Rng(deriveSeed(options.seed ?? 1, 'fuel'));
  const loadRng = new Rng(deriveSeed(options.seed ?? 1, 'load'));
  const phraseRng = new Rng(deriveSeed(options.seed ?? 1, 'phrase'));
  const emergencyRate: EmergencyRate = options.emergencies ?? 'off';
  const emergencyRng = new Rng(deriveSeed(options.seed ?? 1, 'emergency'));
  const emergencies = new EmergencyGenerator(emergencyRng, EMERGENCY_RATES[emergencyRate], emergencyRate === 'normal' ? 2 : 1);
  const aircraft: Aircraft[] = [];
  const stats = newStats();
  const timeline: DebriefEvent[] = [];
  const conflicts = new Map<string, { a: Aircraft; b: Aircraft }>();
  /** Pair keys currently predicted by the short-term conflict alert. */
  let warnings = new Set<string>();
  let stepCount = 0;
  const radio: RadioMessage[] = [];
  /** Acknowledged transmissions waiting out the compliance delay; each is flown whole, in order. */
  const pending: { atS: number; ac: Aircraft; instrs: Instruction[] }[] = [];
  /** Check-ins waiting their turn on the frequency, so several new arrivals do not talk at once. */
  const checkins: { atS: number; ac: Aircraft }[] = [];
  const handlers: { [K in keyof SimEvents]: Set<SimEvents[K]> } = { tick: new Set(), radio: new Set(), event: new Set(), message: new Set() };
  let baseWind: Wind = options.wind ?? { directionDeg: airport.wind.directionDeg, speedKt: airport.wind.speedKt };
  /** Weather that gusts whatever the realism level (a storm); realism 'none' still means calm. */
  let gustyWind = options.gustyWind ?? false;
  const env: EnvState = { windDirDeg: baseWind.directionDeg, windKt: baseWind.speedKt };
  const rwyConfig = runwayConfig(airport, baseWind, options.activeRunways);
  const approachNames = new Map<string, string>();
  /** "ILS 12": the published approach to a runway, else the synthesized one. */
  const approachName = (runway: string) => {
    let name = approachNames.get(runway);
    if (!name) { const a = defaultApproach(airport, runway); name = a ? approachLabel(a) : `RWY ${runway}`; approachNames.set(runway, name); }
    return name;
  };
  let simTimeS = 0, acc = 0, radioId = 1;
  let collisionEnded = false;
  let trainingReadback = options.trainingReadback === true;
  const academyTowerOwned = new Set<number>();
  const academyRepeat = new Map<number, string>();
  let controllerVoiced = false;
  const channel = createRadioChannel();

  const emit = <K extends keyof SimEvents>(k: K, ...args: Parameters<SimEvents[K]>) => { for (const h of handlers[k]) (h as any)(...args); };
  const say = (from: 'atc' | 'pilot', ac: Aircraft | null, phrase: Phrase, kind: RadioKind, extra: Pick<RadioMessage, 'effect' | 'brain'> = {}) => {
    if (ac?.control && ac.control.frequency !== 'tower') return;
    const accent = from === 'atc' || !ac ? airportAccent(airport.icao, airport.xAtc.accent) : accentFor(ac.airline, { icao: airport.icao, ...airport.xAtc });
    const msg: RadioMessage = { id: radioId++, timeS: simTimeS, from, callsign: ac?.callsign ?? '', text: phrase.text, speech: phrase.speech, kind, accent, ...(from === 'pilot' && ac?.crewStyle ? { crewStyle: ac.crewStyle } : {}), ...(from === 'atc' && controllerVoiced && kind === 'command' ? { voiced: true } : {}), ...extra };
    if (options.position === 'tower') {
      const urgent = kind === 'emergency' || /\bgo(?:ing)? around\b|\bstop immediately\b/i.test(phrase.text);
      const slot = channel.enqueue(msg, ac?.id, urgent);
      if (slot.shiftedByS) for (const p of pending) if (p.atS >= slot.startsAtS) p.atS += slot.shiftedByS;
      if (slot.shiftedByS) for (const a of aircraft) if (a.control?.transferAtS !== undefined && a.control.transferAtS >= slot.startsAtS) a.control.transferAtS += slot.shiftedByS;
      return;
    }
    if (ac) { ac.pilot.history.push({ from, text: phrase.text, speech: phrase.speech, timeS: simTimeS }); if (ac.pilot.history.length > 12) ac.pilot.history.shift(); }
    radio.push(msg); if (radio.length > 300) radio.shift();
    emit('radio', msg);
  };
  const tel = () => airport.xAtc.telephony;
  const towerMode = options.position === 'tower';
  if (towerMode) { stats.towerProfile = true; stats.runwayConflicts = 0; }
  let runwayPairs = new Set<string>();
  const operations = createOperations({
    airport, active: () => aircraft, now: () => simTimeS,
    execute(ac, instructions) {
      const plan: InstructionPlan = {};
      for (const i of instructions) {
        const reason = checkInstruction(ac, i, airport, plan) ?? runwayUnable(airport, aircraft, ac, i);
        if (reason) return { ok: false, message: reason };
        if (i.kind === 'heading') plan.headingDeg = toTrueDeg(airport, i.headingDeg);
      }
      runTransmission(ac, instructions, false);
      return { ok: true, message: 'Coordinated instructions accepted.' };
    },
    checkin(ac) { checkins.push({ ac, atS: simTimeS + 2 }); },
    requestTransfer(ac, to) {
      const cs = callsignPhrase(ac, tel());
      const station = to === 'departure' ? airport.radio.dep : to === 'approach' ? airport.radio.app : `${airport.icao} Ground`;
      say('atc', ac, atcPhrase(cs, { text: `contact ${station}`, speech: `contact ${station}` }), 'handoff');
      const bye = farewell();
      say('pilot', ac, pilotPhrase({ text: `contact ${station}, ${bye}`, speech: `contact ${station}, ${bye}` }, cs), 'handoff');
      return channel.freeAtS;
    },
    transfer(ac, to) {
      if (to === 'departure') stats.handoffs++;
      timeline.push({ timeS: simTimeS, type: 'transfer', callsigns: [ac.callsign], detail: `Control transferred to ${to}.` });
    },
  }, towerMode, options.coaching !== false);
  let practicing = false;
  const frames: { timeS: number; aircraft: AircraftState[] }[] = [];
  const streams = [rng, fuelRng, loadRng, phraseRng, emergencyRng];
  const checkpoint = () => structuredClone({
    simTimeS, stepCount, radioId, aircraft, stats, pending, checkins, radio, timeline, conflicts, warnings, runwayPairs,
    streams: streams.map((s) => s.snapshot()), spawner: spawner.snapshot(), emergencyAtS: emergencies.snapshot(), operations: operations.checkpoint(), channel: channel.snapshot(), realism,
  });
  const checkpoints: ReturnType<typeof checkpoint>[] = [];
  function recordReplay() {
    if (stepCount % 20 !== 0) return;
    frames.push({ timeS: simTimeS, aircraft: aircraft.map(toState) });
    if (frames.length > 720) frames.shift();
    if (towerMode && stepCount % 120 === 0) {
      checkpoints.push(checkpoint());
      if (checkpoints.length > 121) checkpoints.shift();
    }
  }

  function currentWind(): Wind {
    if (realism.wind === 'none') return { directionDeg: baseWind.directionDeg, speedKt: 0 };
    if (realism.wind === 'steady' && !gustyWind) return baseWind;
    // Gusts: slow multiplicative noise on speed and a wandering direction.
    const t = simTimeS;
    const g = 1 + 0.3 * Math.sin(t / 17) * Math.sin(t / 5.3) + 0.15 * Math.sin(t / 41);
    return { directionDeg: (baseWind.directionDeg + 12 * Math.sin(t / 97) + 360) % 360, speedKt: Math.max(0, baseWind.speedKt * g) };
  }

  /** Asked out loud now. Without patience the pilot repeats it every two minutes instead of escalating. */
  const vectorsRequest = () => newRequest('vectors', simTimeS, patience ? 0 : 120, !patience);

  const allRunways = airport.runways.flatMap((r) => r.ends.map((e) => e.name));
  const allFixes = [...airport.fixes.keys()];
  /**
   * The values a brain may say about this aircraft now; anything else is refused. An instruction
   * being read back or refused counts too, since it is what the pilot was just told.
   */
  function allowedFor(ac: Aircraft, trigger: PilotTrigger): AllowedValues {
    const nums = (xs: (number | undefined | null)[]) => xs.filter((n): n is number => typeof n === 'number');
    const altitudes = [ac.altitudeFt, Math.round(ac.altitudeFt / 100) * 100, ac.targetAltitudeFt, ac.assigned.altitudeFt];
    // What a pilot may say is magnetic, like everything spoken.
    const mag = (trueDeg: number) => Math.round(toMagneticDeg(airport, trueDeg)) || 360;
    const headings = [mag(ac.headingDeg), mag(ac.nav.headingDeg), ac.assigned.headingDeg];
    const speeds = [Math.round(ac.iasKt), ac.targetIasKt, ac.assigned.speedKt];
    const instr = trigger.kind === 'readback' || trigger.kind === 'unable' ? trigger.instruction : undefined;
    if (instr) {
      if (instr.kind === 'altitude') altitudes.push(instr.altitudeFt);
      else if (instr.kind === 'heading') headings.push(instr.headingDeg);
      else if (instr.kind === 'speed') speeds.push(instr.speedKt);
    }
    if (trigger.kind === 'initiative' && trigger.want === 'sidClimb') altitudes.push(trigger.altitudeFt);
    const numbers = [3];
    if (trigger.kind === 'fuel') numbers.push(Math.max(0, Math.round(trigger.minutesLeft)));
    return {
      altitudesFt: nums(altitudes),
      headingsDeg: nums(headings),
      speedsKt: nums(speeds),
      runways: allRunways,
      fixes: allFixes,
      numbers,
    };
  }

  /**
   * The pilot has something to say. The brain chooses the words and what the crew wants; the sim
   * checks it and applies every consequence. A decision it cannot accept falls back to the rules.
   */
  function speak(ac: Aircraft, trigger: PilotTrigger): void {
    if (ac.control && ac.control.frequency !== 'tower') return;
    const ctx = { ac, airport, realism, simTimeS, telephony: tel(), rng: phraseRng, ...(localStartHour !== undefined ? { localHour: (((localStartHour + simTimeS / 3600) % 24) + 24) % 24 } : {}) };
    const allowed = allowedFor(ac, trigger);
    // The rules answer is worked out first, so it is ready whatever the other brain does.
    const fallback = rulesBrain.decide(trigger, ctx);
    if (towerMode && ['readback', 'unable', 'fuel', 'emergency'].includes(trigger.kind)) {
      applyDecision(ac, trigger, allowed, fallback, fallback, simTimeS);
      return;
    }
    if (!isAsyncBrain(brain)) {
      applyDecision(ac, trigger, allowed, brain.decide(trigger, ctx), fallback, simTimeS);
      return;
    }
    const id = ++brainRequestId;
    const waiting = [...brainPending.entries()].filter(([, p]) => p.ac === ac);
    if (waiting.length >= MAX_PENDING_PER_AIRCRAFT) resolveBrain(waiting[0]![0], undefined, undefined, 'timeout');
    brainPending.set(id, { ac, trigger, allowed, fallback, askedAtS: simTimeS, deadlineS: simTimeS + deadlineFor(trigger, api.timeScale) });
    stats.brainRequests++;
    brain.request({ id, trigger, situation: situationFor(ctx, situationExtras(ac, allowed)), deadlineS: brainPending.get(id)!.deadlineS });
  }

  /** Everything a brain outside the sim may see about this aircraft. */
  function situationExtras(ac: Aircraft, allowed: AllowedValues) {
    return {
      all: aircraft,
      radio: radio.slice(-8).map((m) => ({ from: m.from, callsign: m.callsign, text: m.text, timeS: m.timeS })),
      runways: { arrival: rwyConfig.arrival.runway, departure: rwyConfig.departure.runway, ...(ac.approach ? { approach: approachName(ac.approach.runway.name) } : {}) },
      routeAhead: plannedPath(ac, airport).flatMap((p) => (p.label ? [p.label] : [])),
      allowed,
    };
  }

  /** Check a decision and speak it, or speak the rules answer instead and record why. */
  function applyDecision(ac: Aircraft, trigger: PilotTrigger, allowed: AllowedValues, decision: PilotDecision, fallback: PilotDecision, askedAtS: number, usage?: Record<string, number>, miss?: BrainMiss): void {
    if (ac.control && ac.control.frequency !== 'tower') return;
    let used = decision, why = miss, refusal: string | undefined;
    const check = validateDecision(used, trigger, allowed);
    if (!check.ok) {
      refusal = check.reason;
      stats.brainFallbacks++;
      emit('message', `${ac.callsign}: pilot brain fell back to the rules (${check.reason})`);
      used = { intents: fallback.intents, source: 'fallback' };
      why ??= 'invalid';
      if (!validateDecision(used, trigger, allowed).ok) return;
    }
    const brainNote = { source: used.source, ...(refusal ? { reason: refusal } : why ? { reason: why } : {}) };
    for (const intent of used.intents) applyIntent(ac, intent, trigger, brainNote);
    brainRecord.push({ askedAtS, appliedAtS: simTimeS, callsign: ac.callsign, trigger: trigger.kind, source: used.source, ...(why ? { miss: why } : {}), ...(refusal ? { reason: refusal } : {}), ...(usage ? { usage } : {}) });
    if (brainRecord.length > 500) brainRecord.shift();
  }

  /** An answer arrived, or its deadline did. */
  function resolveBrain(id: number, raw?: unknown, usage?: Record<string, number>, miss?: BrainMiss): void {
    const p = brainPending.get(id);
    if (!p) return;
    brainPending.delete(id);
    if (isAsyncBrain(brain)) brain.cancel?.(id);
    // The aircraft has gone: nobody is left to say it.
    if (isRemovedPhase(p.ac.phase)) return;
    if (p.trigger.kind === 'event' && p.trigger.event === 'requestClimb' && p.ac.assigned.altitudeFt !== undefined) return;
    if (miss === 'timeout') stats.brainTimeouts++;
    const decision = raw === undefined ? { intents: p.fallback.intents, source: 'fallback' as const } : (raw as PilotDecision);
    applyDecision(p.ac, p.trigger, p.allowed, decision, p.fallback, p.askedAtS, usage, miss);
  }

  function applyIntent(ac: Aircraft, intent: PilotIntent, trigger: PilotTrigger, brainNote?: RadioMessage['brain']): void {
    switch (intent.kind) {
      case 'say': {
        const body = intent.phrase ?? renderParts(intent.parts, { ac, airport, telephony: tel() });
        say('pilot', ac, intent.phrase || intent.form === 'plain' ? body : pilotPhrase(body, callsignPhrase(ac, tel())), intent.radio, brainNote ? { brain: brainNote } : {});
        break;
      }
      case 'request': applyRequest(ac, intent.request, trigger); break;
      case 'act':
        if (towerMode) break;
        if (intent.action === 'hold') { if (intent.fix && ac.nav.mode !== 'hold' && !ac.approach) executeInstruction(ac, { kind: 'hold', fix: intent.fix }, airport, realism); }
        else { ac.pilot.request = undefined; setPhase(ac, 'cancelled'); ac.removeAtS = simTimeS + 1; }
        break;
      case 'silence': break;
    }
  }

  /** How long a request waits before it is voiced again depends on why it was made and on patience. */
  function applyRequest(ac: Aircraft, kind: RequestKind, trigger: PilotTrigger): void {
    if (kind === 'climb' && ac.assigned.altitudeFt !== undefined) return;
    if (trigger.kind === 'checkin') { if (patience) ac.pilot.request = newRequest(kind, simTimeS); return; }
    if (kind === 'vectors') { ac.pilot.request = vectorsRequest(); return; }
    if (kind === 'landing') { ac.pilot.request = newRequest('landing', simTimeS); return; }
    ac.pilot.request = newRequest(kind, simTimeS, patience ? 0 : 240);
  }

  function onAircraftEvent(ev: AircraftEvent): void {
    const ac = ev.ac;
    if (ev.type === 'airborne') {
      ac.takeoffAtAgeS = ac.ageS;
      if (ac.assigned.altitudeFt === undefined && !ac.pilot.pending.some((i) => i.kind === 'altitude')) {
        ac.pilot.request = newRequest('climb', simTimeS, patience ? 0 : 240);
        speak(ac, { kind: 'event', event: 'requestClimb' });
      }
    }
    const cs = callsignPhrase(ac, tel());
    if (ev.type === 'goAround') {
      operations.goAround(ac);
      ac.pilot.recovery = 'goAround';
      for (let i = pending.length - 1; i >= 0; i--) if (pending[i]!.ac === ac) pending.splice(i, 1);
      ac.pilot.pending = [];
      ac.pilot.request = newRequest('vectors', simTimeS, 30, true);
    }
    switch (ev.type) {
      case 'touchdown':
        stats.landings++;
        if (ac.emergency) { stats.emergencyLandings++; emit('message', `${ac.callsign} landed with its emergency`); }
        break;
      case 'clearOfRunway':
        speak(ac, { kind: 'event', event: 'clearOfRunway', runway: ev.runway });
        // Back from a rejected take-off: call ready again once at the holding point.
        if (ac.kind === 'departure') checkins.push({ ac, atS: simTimeS + 2 });
        break;
      case 'handoff':
        stats.handoffs++;
        ac.removeAtS ??= simTimeS + 5;
        if (ev.manual) { emit('message', `${ac.callsign} handed off to ${airport.radio.dep}`); break; }
        say('atc', ac, atcPhrase(cs, { text: `contact ${airport.radio.dep}, good day`, speech: `contact ${airport.radio.dep}, good day` }), 'handoff');
        { const bye = farewell(); say('pilot', ac, pilotPhrase({ text: bye, speech: bye }, cs), 'handoff'); }
        emit('message', `${ac.callsign} handed off at ${ev.fix}`);
        break;
      case 'goAround':
        stats.goArounds++;
        if (ev.reasonCode === 'controller') stats.safetyGoArounds++;
        else if (ev.reasonCode === 'unstable') stats.unstableApproaches++;
        else if (ev.reasonCode === 'noClearance') stats.noClearanceGoArounds++;
        else stats.runwayGoArounds++;
        if (ev.reasonCode !== 'controller') speak(ac, { kind: 'event', event: 'goAround', reason: ev.reason });
        emit('message', `${ac.callsign} going around: ${ev.reason}`);
        break;
      case 'missedIntercept':
        stats.missedIntercepts++;
        speak(ac, { kind: 'event', event: 'missedIntercept' });
        emit('message', `${ac.callsign} missed the localizer for runway ${ac.approach?.runway.name ?? ''}`);
        break;
      case 'established':
        if (!ac.approach!.cleared || realism.level === 'high') speak(ac, { kind: 'event', event: 'established', runway: ac.approach!.runway.name, cleared: !!ac.approach!.cleared });
        break;
      case 'requestLanding':
        speak(ac, { kind: 'event', event: 'requestLanding', runway: ac.approach!.runway.name });
        emit('message', `${ac.callsign} on 3 mile final without landing clearance`);
        break;
      case 'reachedFix':
        if (ev.next === 'hold') { speak(ac, { kind: 'event', event: 'clearanceLimit', fix: ev.fix }); ac.lastRequestS = simTimeS; break; }
        if (ev.next === 'offRoute' || (ev.next === 'endOfRoute' && ac.kind === 'arrival' && !ac.approach)) {
          speak(ac, { kind: 'event', event: ev.next === 'offRoute' ? 'offRoute' : 'endOfRoute', fix: ev.fix });
          ac.lastRequestS = simTimeS;
        }
        break;
      case 'abortedTakeoff': stats.abortedTakeoffs++; break;
      case 'exited': stats.exits++; emit('message', `${ac.callsign} left the airspace without a handoff`); break;
    }
    emit('event', ev);
  }

  /** Airport local hour at sim time zero, set by the host (`setLocalClock`); greetings follow it. */
  let localStartHour: number | undefined;
  const farewell = () => variantOf('farewell', phraseRng, flavourOf({ realism }), flavourOf({ realism }) ? airport.xAtc.phraseExtras?.farewell : undefined);
  /** A crew's way of saying its flight number, fixed per callsign so no random stream moves. */
  const radioStyle = radioStyleFor(airport.icao, airport.xAtc.radioStyle);
  const assignCallsignForm = (ac: Aircraft) => {
    const form = callsignFormFor(ac.callsign, radioStyle, flavourOf({ realism }), new Rng(deriveSeed(options.seed ?? 1, `callsign:${ac.callsign}`)).next());
    if (form === 'digits') delete ac.callsignForm; else ac.callsignForm = form;
    // Its own stream, so giving crews a voice moves no traffic, timing or wording elsewhere.
    const style = CREW_STYLES[Math.floor(new Rng(deriveSeed(options.seed ?? 1, `crew:${ac.callsign}`)).next() * CREW_STYLES.length)];
    if (style) ac.crewStyle = style; else delete ac.crewStyle;
  };

  function spawn(ac: Aircraft, checkInDelayS = 0): void {
    aircraft.push(ac);
    assignCallsignForm(ac);
    if (towerMode && options.initialTraffic !== false) operations.prepareArrival(ac);
    operations.initialize(ac);
    ac.persons ??= personsFor(ac, loadRng);
    if (fuelOn && ac.kind === 'arrival' && !ac.fuel) {
      const tight = fuelRng.next() < TIGHT_FUEL_CHANCE[emergencyRate];
      ac.fuel = initialFuel(ac, minutesToLand(ac, airport), fuelRng, tight);
    }
    // The check-in is itself the call that needs an answer (the brain asks for the request).
    // Held back when several aircraft arrive together: the radar sees them at once, the
    // frequency hears them one at a time.
    if (!towerMode) {
      if (checkInDelayS > 0) checkins.push({ atS: simTimeS + checkInDelayS, ac });
      else speak(ac, { kind: 'checkin' });
    }
    emit('event', { type: 'spawn', ac });
  }

  /** Something has gone wrong on board: the flight model keeps flying it, the crew declares it. */
  function beginEmergency(ac: Aircraft, nature: EmergencyNature): void {
    ac.emergency = startEmergency(ac, nature, simTimeS, emergencyRng);
    applyEmergencyPerformance(ac);
    if (nature === 'birdStrike') ac.returning = true;
    // An emergency descent is a crew action, so it does not change the controller's clearance.
    if (nature === 'pressurization') { ac.targetAltitudeFt = Math.max(airport.elevationFt + 3000, 10000); ac.expedite = true; }
    stats.emergencies++;
    const runway = ac.approach?.runway.name ?? rwyConfig.arrival.runway;
    speak(ac, { kind: 'emergency', nature, level: ac.emergency.level, intention: ac.emergency.intention, runway });
    emit('message', `${ac.callsign} declared ${ac.emergency.level === 'mayday' ? 'a MAYDAY' : 'a PAN PAN'}: ${NATURE_TEXT[nature]}`);
    emit('event', { type: 'emergencyDeclared', ac, nature });
  }

  /** The controller has spoken to this aircraft: it answers a check-in and acknowledges an emergency. */
  function answered(ac: Aircraft): void {
    acknowledgeRequest(ac.pilot, simTimeS);
    if (ac.emergency && ac.emergency.acknowledgedAtS === undefined) {
      ac.emergency.acknowledgedAtS = simTimeS;
      emit('event', { type: 'emergencyAcknowledged', ac });
    }
  }

  /**
   * Burn fuel, and tell the controller when the state worsens. A crew never takes back a call:
   * minimum fuel and a fuel emergency escalate and stay.
   */
  const FUEL_ORDER = { normal: 0, minimum: 1, mayday: 2 } as const;
  function updateFuel(ac: Aircraft): void {
    if (!ac.fuel || isRemovedPhase(ac.phase)) return;
    burnFuel(ac, STEP_S);
    if (isOnGroundPhase(ac.phase)) return;
    const runway = ac.approach?.runway.name ?? rwyConfig.arrival.runway;
    if (ac.fuel.enduranceMin <= 0) {
      stats.forcedLandings++;
      speak(ac, { kind: 'fuel', state: 'exhausted', minutesLeft: 0, runway });
      setPhase(ac, 'forcedLanding');
      ac.removeAtS = simTimeS + 2;
      emit('message', `${ac.callsign} ran out of fuel`);
      emit('event', { type: 'forcedLanding', ac });
      return;
    }
    // Working out the margin walks the planned route, so it is not worth doing every step.
    if (simTimeS - (ac.fuel.checkedAtS ?? -Infinity) < FUEL_CHECK_EVERY_S) return;
    ac.fuel.checkedAtS = simTimeS;
    const next = fuelStateFor(fuelMarginMin(ac, airport));
    if (next === 'normal' || FUEL_ORDER[next] <= FUEL_ORDER[ac.fuel.state]) return;
    ac.fuel.state = next;
    ac.fuel.declaredAtS = simTimeS;
    const minutesLeft = ac.fuel.enduranceMin;
    speak(ac, { kind: 'fuel', state: next, minutesLeft, runway });
    if (next === 'minimum') {
      stats.minimumFuelCalls++;
      emit('message', `${ac.callsign} reports minimum fuel: no further delay`);
      emit('event', { type: 'minimumFuel', ac, minutesLeft });
    } else {
      stats.fuelEmergencies++;
      emit('message', `${ac.callsign} declared a fuel emergency`);
      emit('event', { type: 'fuelEmergency', ac, minutesLeft });
    }
  }

  /** Unprompted calls a real pilot would make, at most one every four minutes per aircraft. */
  function pilotInitiatedCall(ac: Aircraft): void {
    if (isRemovedPhase(ac.phase)) return;
    const req = ac.pilot.request;
    if (req?.kind === 'vectors' && req.unvoiced && simTimeS >= req.deferredUntilS && !isOnGroundPhase(ac.phase)) {
      speak(ac, { kind: 'initiative', want: 'vectors' });
      req.askedAtS = req.stageAtS = simTimeS;
      if (patience) req.unvoiced = undefined; else req.deferredUntilS = simTimeS + 120;
      return;
    }
    // A departure held on a SID heading leg by its cleared altitude says so, whatever the initiative
    // setting: without the climb it flies that heading off the scope.
    const sidFloor = sidClimbNeeded(ac);
    if (sidFloor !== undefined && !req && ac.ageS >= 60 && simTimeS - ac.lastRequestS >= 240) {
      ac.lastRequestS = simTimeS;
      speak(ac, { kind: 'initiative', want: 'sidClimb', altitudeFt: sidFloor });
      return;
    }
    // With patience an open request escalates on its own; routine calls wait until it is settled.
    if (pilotInitiative === 'essential' || (req && (patience || simTimeS < req.deferredUntilS))) return;
    if (ac.ageS < 60 || simTimeS - ac.lastRequestS < 240 || isRemovedPhase(ac.phase)) return;
    let want: 'holdingShort' | 'approach' | 'descent' | null = null;
    if (ac.phase === 'holdShort' && ac.phaseT > 120) want = 'holdingShort';
    else if (ac.kind === 'arrival' && ac.phase === 'enroute' && !ac.approach && ac.nav.mode !== 'hold' && ac.assigned.headingDeg === undefined && distanceM(ac.lat, ac.lon, airport.lat, airport.lon) < 15 * NM_TO_M) want = 'approach';
    else if (realism.pilotRequests && ac.kind === 'arrival' && ac.phase === 'enroute' && ac.assigned.altitudeFt === undefined && ac.ageS > 240) want = 'descent';
    if (!want) return;
    ac.lastRequestS = simTimeS;
    speak(ac, { kind: 'initiative', want });
  }

  /** The lowest altitude of the fix after the SID heading leg a departure is flying, when it is cleared below it. */
  function sidClimbNeeded(ac: Aircraft): number | undefined {
    if (ac.kind !== 'departure' || ac.nav.mode !== 'route' || ac.onGround) return undefined;
    const leg = ac.nav.route?.[ac.nav.routeIdx], next = ac.nav.route?.[ac.nav.routeIdx + 1];
    if (leg?.kind !== 'heading' || next?.kind !== 'fix') return undefined;
    const floor = next.restriction?.altitudeMinFt ?? next.restriction?.altitudeFt;
    return floor !== undefined && ac.targetAltitudeFt < floor ? floor : undefined;
  }

  /** An unanswered pilot calls again, presses, then acts: an arrival holds, a departure returns to the stand. */
  function escalate(ac: Aircraft): void {
    const req = ac.pilot.request;
    if (!req || isRemovedPhase(ac.phase)) return;
    // A routine unanswered call never authorizes a change to the clearance.
    if (towerMode) {
      if (ac.control?.frequency !== 'tower' || req.unvoiced || req.stage >= 2) return;
      const stage = dueStage(req, simTimeS, 2);
      if (!stage || stage >= 3) return;
      req.stage = stage; req.stageAtS = simTimeS;
      speak(ac, { kind: 'patience', stage: 1, need: req.kind });
      return;
    }
    // Requests the situation has overtaken (landed, airborne, approach gone) are dropped quietly.
    const stale = isDepartureRequest(req.kind) ? !['holdShort', 'lineUp', 'linedUp'].includes(ac.phase)
      : req.kind === 'landing' ? !ac.approach || isOnGroundPhase(ac.phase)
      : isOnGroundPhase(ac.phase);
    if (stale) { ac.pilot.request = undefined; return; }
    const stage = dueStage(req, simTimeS, realism.pilotPatienceScale ?? 1) as Exclude<PatienceStage, 0> | null;
    if (!stage) return;
    req.stage = stage; req.stageAtS = simTimeS;
    const waitedS = simTimeS - req.askedAtS;
    if (stage === 1) stats.secondCalls++;
    else if (stage === 2) stats.urgentCalls++;
    else stats.pilotActions++;
    if (stage < 3) {
      speak(ac, { kind: 'patience', stage, need: req.kind });
      emit('event', { type: 'pilotWaiting', ac, stage, need: req.kind, waitedS });
      return;
    }
    for (let i = pending.length - 1; i >= 0; i--) if (pending[i]!.ac === ac) pending.splice(i, 1);
    ac.pilot.pending = [];
    if (isDepartureRequest(req.kind)) {
      speak(ac, { kind: 'patience', stage: 3, need: req.kind });
      emit('message', `${ac.callsign} returned to the stand after waiting ${Math.round(waitedS)} s`);
      emit('event', { type: 'pilotWaiting', ac, stage, need: req.kind, waitedS, action: 'returnToStand' });
      return;
    }
    const fix = ac.nav.mode === 'hold' ? ac.nav.hold?.fix : holdFixFor(ac);
    speak(ac, { kind: 'patience', stage: 3, need: req.kind, ...(fix ? { holdFix: fix } : {}) });
    emit('message', `${ac.callsign} ${fix ? `holding at ${fix}` : 'maintaining heading'}: no instructions`);
    emit('event', { type: 'pilotWaiting', ac, stage, need: req.kind, waitedS, action: 'hold', ...(fix ? { fix } : {}) });
  }

  /**
   * An arrival about to cross the airport with no approach clearance and no heading from the
   * controller. It used to fly straight over the field at its inbound level and speed without a
   * word, which reads on the scope as a go-around nobody announced. A real crew would not: it
   * joins a published hold and says so. Only an arrival flying its own heading; one the controller
   * is vectoring, or climbing out of a go-around, carries on.
   */
  function nearFieldUncleared(ac: Aircraft): void {
    if (towerMode || ac.kind !== 'arrival' || ac.onGround || isRemovedPhase(ac.phase) || ac.approach || ac.emergency) return;
    if (ac.nav.mode !== 'heading' || ac.assigned.headingDeg !== undefined || ac.pilot.recovery || ac.phase === 'goAround') return;
    if (ac.pilot.unclearedS !== undefined && simTimeS - ac.pilot.unclearedS < NEAR_FIELD_REPEAT_S) return;
    const toField = distanceM(ac.lat, ac.lon, airport.lat, airport.lon);
    if (toField > NEAR_FIELD_NM * NM_TO_M) return;
    const off = Math.abs(((bearingDeg(ac.lat, ac.lon, airport.lat, airport.lon) - ac.trackDeg + 540) % 360) - 180);
    if (off > 60) return;
    ac.pilot.unclearedS = simTimeS;
    const fix = holdFixFor(ac, true);
    for (let i = pending.length - 1; i >= 0; i--) if (pending[i]!.ac === ac) pending.splice(i, 1);
    ac.pilot.pending = [];
    if (fix) executeInstruction(ac, { kind: 'hold', fix }, airport, realism);
    speak(ac, { kind: 'event', event: 'noApproachClearance', ...(fix ? { fix } : {}) });
    emit('message', `${ac.callsign} reached the field without an approach clearance${fix ? `; holding at ${fix}` : ''}`);
  }

  /**
   * Where a neglected arrival holds: the next fix on its filed route, else the nearest published
   * fix. With `free`, a fix where another aircraft already holds within 1,000 ft is passed over
   * for the next nearest, so a bank of neglected arrivals does not stack itself into a conflict.
   */
  function holdFixFor(ac: Aircraft, free = false): string | undefined {
    const taken = (name: string) => free && aircraft.some((o) => o !== ac && !isRemovedPhase(o.phase) && o.nav.mode === 'hold' && o.nav.hold?.fix === name && Math.abs(o.altitudeFt - ac.altitudeFt) < 1000);
    const leg = ac.flightPlan[rejoinIndex(ac, airport)];
    if (leg?.kind === 'fix' && airport.fixes.has(leg.fix) && !taken(leg.fix)) return leg.fix;
    const near = [...airport.fixes.values()].filter((f) => !f.hidden)
      .map((f) => ({ name: f.name, m: distanceM(ac.lat, ac.lon, f.lat, f.lon) }))
      .sort((a, b) => a.m - b.m);
    return (near.find((f) => !taken(f.name)) ?? near[0])?.name;
  }

  function fixedStep(): void {
    simTimeS += STEP_S;
    stats.elapsedS = simTimeS;
    operations.step();
    if (towerMode) channel.deliver(simTimeS, ({ message, aircraftId, startsAtS }) => {
      const ac = aircraft.find((a) => a.id === aircraftId);
      if (aircraftId !== undefined && (!ac || ac.control?.frequency !== 'tower')) return;
      const msg = { ...message, timeS: startsAtS };
      if (ac) {
        ac.pilot.history.push({ from: msg.from, text: msg.text, speech: msg.speech, timeS: startsAtS });
        if (ac.pilot.history.length > 12) ac.pilot.history.shift();
      }
      radio.push(msg); if (radio.length > 300) radio.shift(); emit('radio', msg);
    });
    // Answers that came back since the last step, then the ones that ran out of time.
    for (const item of brainInbox.splice(0)) resolveBrain(item.id, item.decision, item.usage);
    for (const id of [...brainPending.keys()]) if (simTimeS >= brainPending.get(id)!.deadlineS) resolveBrain(id, undefined, undefined, 'timeout');
    // Delayed instructions.
    for (let i = 0; i < pending.length;) {
      const p = pending[i]!;
      if (p.atS > simTimeS) { i++; continue; }
      pending.splice(i, 1);
      if (!isRemovedPhase(p.ac.phase)) runTransmission(p.ac, p.instrs, true);
    }
    const occupied = occupiedRunways(airport, aircraft);
    const before = new Map<number, Position>(aircraft.map((ac) => [ac.id, { lat: ac.lat, lon: ac.lon, altitudeFt: ac.altitudeFt }]));
    const wind = currentWind(); env.windDirDeg = wind.directionDeg; env.windKt = wind.speedKt;
    const ctx: StepContext = { airport, opts: realism, wind, simTimeS, occupiedRunways: occupied, traffic: aircraft, emit: onAircraftEvent };
    for (const ac of aircraft) {
      if (ac.phase === 'holdShort' || ac.phase === 'linedUp') stats.departureWaitS += STEP_S;
      stepAircraft(ac, STEP_S, ctx);
      // A touchdown or line-up in this step must protect later aircraft in the same step.
      ctx.occupiedRunways = occupiedRunways(airport, aircraft);
      if (!isOnGroundPhase(ac.phase) && !isRemovedPhase(ac.phase) && distanceM(ac.lat, ac.lon, airport.lat, airport.lon) > (scopeRadiusNm + 3) * NM_TO_M) {
        setPhase(ac, 'exited'); ac.removeAtS = simTimeS + 1;
        if (!ac.control || ac.control.owner === 'tower') onAircraftEvent({ type: 'exited', ac });
      }
      nearFieldUncleared(ac);
      if (fuelOn) updateFuel(ac);
      if (!towerMode || ac.control?.frequency === 'tower') pilotInitiatedCall(ac);
      if (patience) escalate(ac);
    }
    const impact = detectImpacts(aircraft, before)[0];
    if (impact) {
      stats.collisions++;
      const runway = impact.a.approach?.runway.name ?? impact.b.approach?.runway.name ?? impact.a.departureRunway?.name ?? impact.b.departureRunway?.name;
      const detail = impact.kind === 'air' ? `Aircraft collided in flight near ${Math.round(impact.altitudeFt / 100) * 100} ft.` : impact.kind === 'runway' ? `An aircraft struck traffic on or just above runway ${runway ?? 'the airport'}.` : `Aircraft collided on the ground${runway ? ` near runway ${runway}` : ''}.`;
      timeline.push({ timeS: simTimeS, type: 'collision', callsigns: [impact.a.callsign, impact.b.callsign], detail });
      for (const ac of [impact.a, impact.b]) { setPhase(ac, 'crashed'); ac.iasKt = ac.groundspeedKt = ac.verticalSpeedFpm = 0; ac.removeAtS = undefined; }
      stats.score = computeScore(stats);
      frames.push({ timeS: simTimeS, aircraft: aircraft.map(toState) });
      collisionEnded = true;
      api.paused = true;
      emit('event', { type: 'collision', ...impact });
      emit('message', `COLLISION: ${impact.a.callsign} / ${impact.b.callsign}`);
      return;
    }
    // Separation.
    if (towerMode) {
      const occupied = aircraft.flatMap((ac) => { const runway = occupiedEnd(ac, airport); return runway ? [{ ac, runway }] : []; });
      const nowPairs = new Set<string>();
      for (let i = 0; i < occupied.length; i++) for (let j = i + 1; j < occupied.length; j++) {
        const a = occupied[i]!, b = occupied[j]!;
        if (!runwaysIntersect(airport, a.runway, b.runway)) continue;
        const key = `${a.ac.id}/${b.ac.id}`; nowPairs.add(key);
        if (!runwayPairs.has(key)) {
          stats.runwayConflicts = (stats.runwayConflicts ?? 0) + 1;
          timeline.push({ timeS: simTimeS, type: 'runwayConflict', callsigns: [a.ac.callsign, b.ac.callsign], detail: 'Conflicting runway operations. Protect occupancy and crossing permissions before issuing another runway clearance.' });
          emit('message', `RUNWAY CONFLICT: ${a.ac.callsign} / ${b.ac.callsign}`);
        }
      }
      runwayPairs = nowPairs;
    }
    const found = findConflicts(aircraft, realism.wakeSeparation, airport);
    const seen = new Set<string>();
    for (const c of found) {
      seen.add(c.key);
      if (!conflicts.has(c.key)) { conflicts.set(c.key, { a: c.a, b: c.b }); stats.conflicts++; emit('event', { type: 'conflictStart', a: c.a, b: c.b, wake: c.wake }); emit('message', `SEPARATION: ${c.a.callsign} / ${c.b.callsign}${c.wake ? ' (wake)' : ''}`); }
      stats.conflictSeconds += STEP_S;
    }
    for (const [key, pair] of conflicts) if (!seen.has(key)) { conflicts.delete(key); emit('event', { type: 'conflictEnd', a: pair.a, b: pair.b }); }
    for (const ac of aircraft) ac.conflict = false;
    for (const c of found) { c.a.conflict = true; c.b.conflict = true; }
    // Short-term conflict alert, once a sim second; pairs already in conflict stay red instead.
    if (++stepCount % 4 === 0) {
      const next = new Set<string>();
      for (const ac of aircraft) ac.conflictWarning = false;
      for (const p of predictConflicts(aircraft, 60, airport, realism, currentWind(), pending.map((p) => ({ ...p, inS: p.atS - simTimeS })))) {
        if (conflicts.has(p.key)) continue;
        next.add(p.key);
        p.a.conflictWarning = true; p.b.conflictWarning = true;
        if (!warnings.has(p.key)) emit('event', { type: 'conflictWarning', a: p.a, b: p.b, inS: p.inS });
      }
      warnings = next;
    }
    // Removals.
    for (let i = aircraft.length - 1; i >= 0; i--) {
      const ac = aircraft[i]!;
      if (ac.removeAtS !== undefined && simTimeS >= ac.removeAtS) { aircraft.splice(i, 1); emit('event', { type: 'removed', ac }); }
    }
    // Queued check-ins.
    for (let i = 0; i < checkins.length;) {
      const c = checkins[i]!;
      if (c.atS > simTimeS) { i++; continue; }
      if (c.ac.phase === 'taxiOut') { c.atS = simTimeS + 1; i++; continue; }
      checkins.splice(i, 1);
      if (!isRemovedPhase(c.ac.phase)) speak(c.ac, { kind: 'checkin' });
    }
    // Spawns.
    if (options.trafficRamp) {
      const overloaded = conflicts.size > 0 || aircraft.filter((a) => a.phase === 'holdShort' || a.phase === 'linedUp').length >= 3;
      {
        const bankTime = simTimeS % 900;
        const factor = towerMode ? bankTime < 180 ? 0.65 : bankTime < 600 ? 1.15 : 0.8 : overloaded ? 0.5 : Math.min(1, 0.5 + simTimeS / 1200);
        spawner.options.arrivalsPerHour = (options.arrivalsPerHour ?? 24) * factor * operations.arrivalFactor;
        spawner.options.departuresPerHour = (options.departuresPerHour ?? 18) * factor;
      }
    }
    if (towerMode && !options.trafficRamp) spawner.options.arrivalsPerHour = (options.arrivalsPerHour ?? 24) * operations.arrivalFactor;
    const trouble = emergencies.update(simTimeS, aircraft, airport.elevationFt);
    if (trouble) beginEmergency(trouble.ac, trouble.nature);
    for (const ac of spawner.update(simTimeS, aircraft, realism, maxAircraft)) spawn(ac);
    stats.score = computeScore(stats);
    recordReplay();
  }

  function toState(ac: Aircraft): AircraftState {
    return {
      ...(ac.control ? { control: { ...ac.control } } : {}),
      callsign: ac.callsign, lat: ac.lat, lon: ac.lon, altitudeFt: ac.altitudeFt, headingDeg: ac.headingDeg, groundspeedKt: ac.groundspeedKt,
      verticalSpeedFpm: ac.verticalSpeedFpm, type: ac.type, onGround: ac.onGround, category: ac.category,
      kind: ac.kind, airline: ac.airline, phase: ac.phase, requested: ac.requested, assigned: { ...ac.assigned }, conflict: ac.conflict, conflictWarning: ac.conflictWarning, iasKt: ac.iasKt,
      pending: pending.some((p) => p.ac === ac),
      waiting: ac.pilot.request && !ac.pilot.request.unvoiced ? { need: NEEDS[ac.pilot.request.kind].code, sinceS: ac.pilot.request.askedAtS, stage: ac.pilot.request.stage } : undefined,
      procedure: ac.procedure,
      arrivalRunway: ac.arrivalRunway?.name,
      departureRunway: ac.departureRunway?.name,
      approach: ac.approach ? approachName(ac.approach.runway.name) : undefined,
      ...(ac.approach?.cleared ? { landingCleared: true } : {}),
      emergency: ac.emergency ? { code: NATURE_CODE[ac.emergency.nature], level: ac.emergency.level } : undefined,
      fuelState: ac.fuel && ac.fuel.state !== 'normal' ? ac.fuel.state : undefined,
    };
  }

  /** Fly an accepted transmission in order. `recheck` after a delay: a pilot who can no longer comply says so. */
  function runTransmission(ac: Aircraft, instrs: Instruction[], recheck: boolean): void {
    const ctx = { altitudeInTransmission: instrs.some((i) => i.kind === 'altitude'), speedInTransmission: instrs.some((i) => i.kind === 'speed') };
    for (const instr of instrs) {
      ac.pilot.pending = ac.pilot.pending.filter((i) => i !== instr);
      const reason = (recheck ? recheckInstruction(ac, instr, airport) : null) ?? (towerMode ? options.coaching === false ? null : towerRunwayRisk(airport, aircraft, ac, instr) : runwayUnable(airport, aircraft, ac, instr));
      if (reason) {
        speak(ac, { kind: 'unable', instruction: instr, reason });
        emit('message', `${ac.callsign}: unable, ${reason}`);
        emit('event', { type: 'unable', ac, instruction: instr, reason });
        continue;
      }
      const ev = executeInstruction(ac, instr, airport, realism, ctx);
      if (ev) onAircraftEvent(ev);
    }
  }

  /** A newer transmission replaces anything still queued for the same aircraft on the same axis. */
  function supersede(ac: Aircraft, instrs: Instruction[]): void {
    const axes = new Set(instrs.map(instructionAxis));
    ac.pilot.pending = ac.pilot.pending.filter((i) => !axes.has(instructionAxis(i)));
    if (instrs.some((i) => i.kind === 'abort')) {
      for (let i = pending.length - 1; i >= 0; i--) if (pending[i]!.ac === ac) pending.splice(i, 1);
      ac.pilot.pending = [];
    }
    for (let i = pending.length - 1; i >= 0; i--) {
      const p = pending[i]!;
      if (p.ac !== ac) continue;
      p.instrs = p.instrs.filter((x) => !axes.has(instructionAxis(x)));
      if (p.instrs.length === 0) pending.splice(i, 1);
    }
  }

  function handleAircraftCommand(callsign: string, instructions: Instruction[]): CommandResult {
    const ac = aircraft.find((a) => a.callsign === callsign);
    if (!ac) { stats.rejectedCommands++; return { ok: false, status: 'rejected', accepted: [], rejected: instructions, message: `${callsign}: no such flight` }; }
    if (options.academyApproach && academyTowerOwned.has(ac.id)) return { ok: false, status: 'rejected', message: `${callsign} is with tower now.` };
    if (options.academyApproach) {
      // Runway clearances belong to tower. The rest of the transmission (typically the intercept
      // heading said in the same breath as "cleared to land") is still flown, and the coach says
      // what approach clears instead.
      const runwayClearances = instructions.filter((i) => RUNWAY_CLEARANCES.includes(i.kind));
      if (runwayClearances.length) {
        const rw = runwayClearances.find((i) => i.kind === 'land')?.runway;
        const coach = rw
          ? `Coach: approach does not clear to land. Clear the ILS (${callsign} I ${rw}), then transfer it to tower once established (${callsign} CTW).`
          : 'Coach: approach does not issue take-off or line-up clearances.';
        const rest = instructions.filter((i) => !RUNWAY_CLEARANCES.includes(i.kind));
        if (!rest.length) return { ok: false, status: 'rejected', accepted: [], rejected: instructions, message: coach };
        const r = handleAircraftCommand(callsign, rest);
        return { ...r, status: r.ok ? 'partial' : r.status, rejected: [...(r.rejected ?? []), ...runwayClearances], message: `${r.message}. ${coach}` };
      }
    }
    const positionReason = operations.canTransmit(ac) ?? operations.instructionReason(ac, instructions);
    if (positionReason) return { ok: false, status: 'rejected', accepted: [], rejected: instructions, message: positionReason };
    if (towerMode && instructions.length === 1 && instructions[0]!.kind === 'contact') return api.coordinate({ kind: 'transfer', callsign, to: ac.control?.offeredTo ?? 'departure' });
    if (towerMode && JSON.stringify(ac.pilot.pending) === JSON.stringify(instructions)) return { ok: true, status: 'accepted', accepted: instructions, rejected: [], message: `${callsign}: clearance already queued on the frequency.` };
    if (isRemovedPhase(ac.phase)) { stats.rejectedCommands++; return { ok: false, status: 'rejected', accepted: [], rejected: instructions, message: `${callsign}: no longer on frequency` }; }
    // Being spoken to answers a check-in and restarts the pilot's wait, whatever the instruction.
    answered(ac);
    const said: Instruction[] = [], flown: Instruction[] = [], messages: string[] = [];
    const plan: InstructionPlan = {};
    let rejection: string | null = null;
    for (const original of instructions) {
      const runwayRisk = towerMode ? towerRunwayRisk(airport, aircraft, ac, original) : runwayUnable(airport, aircraft, ac, original);
      if (towerMode && options.coaching !== false && runwayRisk) {
        rejection = `Coach: ${runwayRisk}. Wait for the runway to clear or instruct a go-around.`;
        break;
      }
      const inactive = (original.kind === 'land' || original.kind === 'approach') && findRunwayEnd(airport, original.runway) && !rwyConfig.arrival.runways.includes(original.runway) ? `runway ${original.runway} is not active for arrivals` : null;
      const reason = inactive ?? checkInstruction(ac, original, airport, plan) ?? (towerMode ? null : runwayRisk);
      if (reason) {
        stats.rejectedCommands++;
        say('atc', ac, phraseInstruction(ac, original, airport, tel()).atc, 'command', { effect: { tone: 'refused', text: `unable: ${reason}` } });
        speak(ac, { kind: 'unable', instruction: original, reason });
        emit('event', { type: 'unable', ac, instruction: original, reason });
        rejection = reason;
        break;
      }
      // High realism: occasionally the pilot hears a different number and flies it.
      const numeric = ['heading', 'altitude', 'speed'].includes(original.kind);
      const scripted = trainingReadback && numeric;
      const instr = scripted || (realism.wrongReadbackRate > 0 && rng.next() < realism.wrongReadbackRate) ? garbleReadback(original, rng) : original;
      if (scripted) trainingReadback = false;
      const ph = phraseInstruction(ac, instr, airport, tel());
      // What was said, not what a garbled readback will fly: catching that is the player's job.
      say('atc', ac, phraseInstruction(ac, original, airport, tel()).atc, 'command', { effect: { tone: 'done', text: instructionSummary(original).text } });
      speak(ac, { kind: 'readback', instruction: instr });
      said.push(original); flown.push(instr);
      messages.push(ph.message.replace(`${ac.callsign}: `, ''));
      if (instr.kind === 'heading') plan.headingDeg = toTrueDeg(airport, instr.headingDeg);
      else if (instr.kind === 'direct') { const f = airport.fixes.get(instr.fix)!; plan.headingDeg = bearingDeg(ac.lat, ac.lon, f.lat, f.lon); }
    }
    // Instructions before a rejection stand. The whole transmission is flown after one delay, in
    // order; on the ground and for go-arounds it acts at once.
    if (flown.length) {
      supersede(ac, flown);
      ac.pilot.heard = flown.map((i) => ({ ...i }));
      if (flown.some((i) => ['heading', 'direct', 'hold', 'resumeNav', 'approach', 'land'].includes(i.kind))) ac.pilot.recovery = undefined;
      const req = ac.pilot.request;
      const outcome = req ? requestOutcome(req.kind, flown) : null;
      if (outcome === 'clear') ac.pilot.request = undefined;
      else if (outcome === 'lineUp') ac.pilot.request = patience ? newRequest('lineUp', simTimeS) : undefined;
      const [dMin, dMax] = realism.complianceDelayS;
      const delay = dMin === dMax ? dMin : rng.range(dMin, dMax);
      const deliveryDelay = towerMode ? Math.max(0, channel.freeAtS - simTimeS) : 0;
      if ((!towerMode && (delay <= 0 || isOnGroundPhase(ac.phase))) || flown.some((i) => i.kind === 'abort')) runTransmission(ac, flown, false);
      else { pending.push({ atS: simTimeS + Math.max(delay, deliveryDelay), ac, instrs: flown }); ac.pilot.pending.push(...flown); }
      emit('event', { type: 'command', ac, instructions: said });
    }
    if (flown.length) stats.commands++;
    if (rejection) return { ok: false, status: said.length ? 'partial' : 'rejected', accepted: said, rejected: instructions.slice(said.length), message: `${ac.callsign}: unable, ${rejection}${messages.length ? ` (accepted: ${messages.join('; ')}; later instructions not accepted)` : ''}` };
    return { ok: true, status: 'accepted', accepted: said, rejected: [], message: `${ac.callsign}: ${messages.join(', ')}` };
  }

  const api: Sim = {
    replay: () => structuredClone(frames),
    get practicing() { return practicing; },
    practiceAt(timeS) {
      const saved = checkpoints.filter((p) => p.simTimeS <= timeS).at(-1);
      if (!towerMode || !saved || !Number.isFinite(timeS)) return false;
      for (const id of brainPending.keys()) if (isAsyncBrain(brain)) brain.cancel?.(id);
      brainPending.clear(); brainInbox.length = 0; brain = rulesBrain;
      const p = structuredClone(saved);
      simTimeS = p.simTimeS; stepCount = p.stepCount; radioId = p.radioId; acc = 0;
      collisionEnded = false;
      aircraft.splice(0, aircraft.length, ...p.aircraft); Object.assign(stats, p.stats);
      pending.splice(0, pending.length, ...p.pending); checkins.splice(0, checkins.length, ...p.checkins);
      radio.splice(0, radio.length, ...p.radio); timeline.splice(0, timeline.length, ...p.timeline);
      conflicts.clear(); for (const [key, value] of p.conflicts) conflicts.set(key, value);
      warnings = p.warnings; runwayPairs = p.runwayPairs; streams.forEach((s, i) => s.restore(p.streams[i]!));
      spawner.restore(p.spawner); emergencies.restore(p.emergencyAtS); operations.restore(p.operations); channel.restore(p.channel); realism = p.realism;
      for (let i = frames.length - 1; i >= 0; i--) if (frames[i]!.timeS > simTimeS) frames.splice(i, 1);
      for (let i = checkpoints.length - 1; i >= 0; i--) if (checkpoints[i]!.simTimeS > simTimeS) checkpoints.splice(i, 1);
      practicing = true;
      return true;
    },
    operations: () => operations.snapshot(),
    coordinate(request) {
      if (!request || typeof request !== 'object') return { ok: false, message: 'Unsupported coordination request.' };
      const result = operations.coordinate(request);
      if (result.ok && 'callsign' in request && request.kind !== 'accept') {
        for (let i = pending.length - 1; i >= 0; i--) if (pending[i]!.ac.callsign === request.callsign) pending.splice(i, 1);
      }
      emit('message', result.message);
      return result;
    },
    airport,
    get simTimeS() { return simTimeS; },
    timeScale: 1,
    paused: false,
    get realism() { return realism; },
    approachRole: options.academyApproach === true,
    env,
    step(wallDtS) {
      if (!api.paused && !collisionEnded) {
        acc += Math.min(wallDtS, 1) * api.timeScale;
        let guard = 0;
        while (acc >= STEP_S && !api.paused && guard++ < 400) { acc -= STEP_S; fixedStep(); }
      }
      emit('tick', aircraft.map(toState), simTimeS);
    },
    command(line) {
      if (options.academyApproach) {
        const handoff = /^([A-Z0-9]{2,8})\s+CTW$/i.exec(line.trim());
        if (handoff) {
          const ac = aircraft.find((a) => a.callsign === handoff[1]!.toUpperCase() && !isRemovedPhase(a.phase));
          if (!ac || ac.kind !== 'arrival') return { ok: false, message: 'No arrival to transfer.' };
          if (academyTowerOwned.has(ac.id)) return { ok: true, message: `${ac.callsign} is already with tower.` };
          if (!ac.approach?.established) return { ok: false, message: `${ac.callsign} must be established on the approach before tower transfer.` };
          if (ac.conflict || ac.conflictWarning) return { ok: false, message: `Resolve ${ac.callsign}'s traffic conflict before transfer.` };
          const landing = { kind: 'land' as const, runway: ac.approach.runway.name };
          const risk = checkInstruction(ac, landing, airport, {}) ?? runwayUnable(airport, aircraft, ac, landing);
          if (risk) return { ok: false, message: `Tower cannot clear ${ac.callsign} to land: ${risk}` };
          academyTowerOwned.add(ac.id);
          const cs = callsignPhrase(ac, tel());
          say('atc', ac, atcPhrase(cs, { text: `contact ${airport.icao} tower`, speech: `contact ${airport.icao} tower` }), 'handoff');
          say('pilot', ac, pilotPhrase({ text: 'contact tower', speech: 'contact tower' }, cs), 'handoff');
          say('atc', ac, phraseInstruction(ac, landing, airport, tel()).atc, 'command');
          runTransmission(ac, [landing], false);
          emit('event', { type: 'academyTowerHandoff', ac });
          return { ok: true, message: `${ac.callsign} transferred to tower; landing clearance issued by tower.` };
        }
      }
      if (towerMode) {
        const coordination = /^([A-Z0-9]{2,8})\s+(ACCEPT|CG|CA|HP|CROSS)$/i.exec(line.trim());
        if (coordination) {
          const callsign = coordination[1]!.toUpperCase(), kind = coordination[2]!.toUpperCase();
          return api.coordinate(kind === 'ACCEPT' ? { kind: 'accept', callsign } : kind === 'HP' ? { kind: 'holdPosition', callsign } : kind === 'CROSS' ? { kind: 'crossRunway', callsign } : { kind: 'transfer', callsign, to: kind === 'CG' ? 'ground' : 'approach' });
        }
        const flow = /^FLOW\s+(NORMAL|SPACED|SUSPENDED)$/i.exec(line.trim());
        if (flow) return api.coordinate({ kind: 'arrivalFlow', mode: flow[1]!.toLowerCase() as 'normal' | 'spaced' | 'suspended' });
      }
      const query = /^([A-Z0-9]{2,8})\s+(?:Q\s+(instructions|heading|altitude|speed|assignedHeading|assignedAltitude|assignedSpeed|landing|intentions|personsOnBoard|fuelRemaining)|(STANDBY|SAYAGAIN|RC))$/i.exec(line.trim());
      if (query) {
        const subjects = [...QUERY_SUBJECTS];
        return api.transmission({ callsign: query[1]!.toUpperCase(), messages: [query[2] ? { kind: 'query', subject: subjects.find((s) => s.toLowerCase() === query[2]!.toLowerCase()) } : { kind: ({ STANDBY: 'standby', SAYAGAIN: 'repeat', RC: 'acknowledge' } as const)[query[3]!.toUpperCase() as 'STANDBY' | 'SAYAGAIN' | 'RC'] }] });
      }
      const parsed = parseCommand(line);
      if (!parsed.ok) { stats.rejectedCommands++; emit('message', parsed.message); return { ok: false, message: parsed.message }; }
      const c = parsed.command;
      let result: CommandResult;
      if (c.type === 'aircraft') result = handleAircraftCommand(c.callsign, c.instructions);
      else {
        const s = c.command;
        switch (s.kind) {
          case 'pause': api.paused = !api.paused; result = { ok: true, message: api.paused ? 'Paused' : 'Resumed', system: s }; break;
          case 'timeScale': api.timeScale = s.factor; result = { ok: true, message: `Sim speed ×${s.factor}`, system: s }; break;
          case 'realism': api.setRealism(s.level); result = { ok: true, message: `Realism: ${s.level}`, system: s }; break;
          case 'stats': result = { ok: true, message: 'Statistics', system: s, text: statsText(api.stats()) }; break;
          case 'help': result = { ok: true, message: 'Help', system: s, text: HELP_TEXT + '\n\nPilot conversation\n' + PILOT_HELP + (towerMode ? '\n\nTower coordination\nID ACCEPT: accept inbound transfer\nID CG / CA / CT: contact ground / approach / departure\nID HP: hold position before takeoff roll\nID CROSS: approve ground crossing\nFLOW NORMAL / SPACED / SUSPENDED: coordinate new arrivals' : '') }; break;
          case 'view': result = { ok: true, message: `Display: ${s.layout}`, system: s }; break;
          case 'fullscreen': result = { ok: true, message: 'Full screen', system: s }; break;
          case 'brief': result = { ok: true, message: 'Airport briefing', system: s }; break;
          default: result = { ok: true, message: s.kind.toUpperCase(), system: s };
        }
      }
      emit('message', result.message);
      return result;
    },
    configurePilots(opts) { if (opts.initiative) pilotInitiative = opts.initiative; if (opts.shortCallsigns !== undefined) spawner.options.shortCallsigns = opts.shortCallsigns; },
    pilotContext(callsign) {
      const ac = aircraft.find((a) => a.callsign === callsign && !isRemovedPhase(a.phase));
      const state = api.context().find((a) => a.callsign === callsign);
      return ac && state ? { state, memory: structuredClone(ac.pilot), simTimeS } : null;
    },
    transmission(raw) {
      const tx = validateTransmission(raw);
      if (!tx) return { ok: false, message: 'Say again, unsupported transmission.' };
      const ac = aircraft.find((a) => a.callsign === tx.callsign && !isRemovedPhase(a.phase));
      if (!ac) return { ok: false, message: 'Aircraft is not on frequency.' };
      if (options.academyApproach && academyTowerOwned.has(ac.id)) return { ok: false, message: `${ac.callsign} is with tower now.` };
      const positionReason = operations.canTransmit(ac);
      if (positionReason) return { ok: false, message: positionReason };
      let result: CommandResult = { ok: true, message: '' };
      for (let n = 0; n < tx.messages.length; n++) {
        const m = tx.messages[n]!;
        if (m.kind === 'instruction') {
          const instructions: Instruction[] = [m.instruction];
          while (tx.messages[n + 1]?.kind === 'instruction') { const next = tx.messages[++n]!; if (next.kind === 'instruction') instructions.push(next.instruction); }
          result = handleAircraftCommand(ac.callsign, instructions);
          if (!result.ok) return result;
          continue;
        }
        if (m.kind === 'towerTransfer') {
          result = api.command(`${ac.callsign} CTW`);
          if (!result.ok) return result;
          continue;
        }
        if (m.kind === 'query') {
          say('atc', ac, atcPhrase(callsignPhrase(ac, tel()), { text: `say ${m.subject.replace(/([A-Z])/g, ' $1').toLowerCase()}`, speech: `say ${m.subject.replace(/([A-Z])/g, ' $1').toLowerCase()}` }), 'command', { effect: { tone: 'talk', text: 'question · nothing changed' } });
          answered(ac);
          emit('event', { type: 'pilotQuery', ac, subject: m.subject });
          speak(ac, { kind: 'answer', subject: m.subject });
        } else if (m.kind === 'conversation') {
          const ask = clarificationFor(m.topic, m.text);
          const effect: RadioEffect = !ask ? { tone: 'talk', text: 'talk only · nothing changed' }
            : { tone: 'refused', text: ask === 'instruction' ? 'not flown · not an instruction the crew can fly' : `not flown · no ${ask} given` };
          say('atc', ac, atcPhrase(callsignPhrase(ac, tel()), { text: m.text, speech: m.text }), 'command', { effect });
          answered(ac);
          speak(ac, { kind: 'conversation', topic: m.topic, text: m.text });
        } else if (m.kind === 'standby') {
          say('atc', ac, atcPhrase(callsignPhrase(ac, tel()), { text: 'stand by', speech: 'stand by' }), 'command', { effect: { tone: 'talk', text: 'stand by · nothing changed' } });
          const req = ac.pilot.request;
          if (req && req.standbys < STANDBY_LIMIT) { req.standbys++; req.deferredUntilS = simTimeS + STANDBY_S; }
          answered(ac);
          speak(ac, { kind: 'reply', to: 'standby' });
        } else if (m.kind === 'acknowledge') {
          const contact = ac.kind === 'arrival' && !isOnGroundPhase(ac.phase);
          say('atc', ac, atcPhrase(callsignPhrase(ac, tel()), { text: contact ? 'radar contact' : 'roger', speech: contact ? 'radar contact' : 'roger' }), 'command', { effect: { tone: 'talk', text: 'acknowledged · nothing changed' } });
          answered(ac);
          speak(ac, { kind: 'reply', to: 'acknowledge' });
        } else {
          say('atc', ac, atcPhrase(callsignPhrase(ac, tel()), { text: 'say again', speech: 'say again' }), 'command');
          const last = [...ac.pilot.history].reverse().find((h) => h.from === 'pilot');
          const replay = academyRepeat.get(ac.id);
          if (replay) academyRepeat.delete(ac.id);
          const full = replay ? { text: replay, speech: replay } : last ? { text: last.text, speech: last.speech } : pilotPhrase({ text: 'no previous transmission', speech: 'no previous transmission' }, callsignPhrase(ac, tel()));
          say('pilot', ac, full, 'report'); result = { ok: true, message: full.text }; continue;
        }
        // The brain has spoken; the command area echoes what the pilot said.
        result = { ok: true, message: radio.at(-1)?.text ?? '' };
      }
      emit('message', result.message);
      return result;
    },
    timeline() { return Object.freeze(timeline.map((e) => Object.freeze({ ...e, callsigns: Object.freeze([...e.callsigns]) as unknown as string[] }))); },
    clearance(proposal) {
      const parsed = validateClearance(proposal);
      if (!parsed.ok) { stats.rejectedCommands++; return { ...parsed, status: 'rejected', accepted: [] }; }
      const result = handleAircraftCommand(parsed.clearance.callsign, parsed.clearance.instructions);
      emit('message', result.message);
      return result;
    },
    aircraft() { return aircraft.map(toState); },
    context() { return Object.freeze(aircraft.map((ac) => Object.freeze({ ...toState(ac), phase: ac.phase, kind: ac.kind, airline: ac.airline, assigned: Object.freeze({ ...ac.assigned }), approachRunway: ac.approach?.runway.name, arrivalRunway: ac.arrivalRunway?.name, established: !!ac.approach?.established, landingCleared: !!ac.approach?.cleared, departureRunway: ac.departureRunway?.name, occupiedRunway: occupiedEnd(ac, airport)?.name }))); },
    internal() { return aircraft; },
    find(callsign) { return aircraft.find((a) => a.callsign === callsign); },
    runwayConfig() { return rwyConfig; },
    setBrain(next) {
      // Anything still outstanding is answered by the rules brain rather than left hanging.
      for (const id of [...brainPending.keys()]) resolveBrain(id, undefined, undefined, 'stale');
      brain = next;
    },
    get brainId() { return brain.id; },
    deliverDecision(id, decision, usage) { if (brainPending.has(id)) brainInbox.push({ id, decision, ...(usage ? { usage } : {}) }); },
    brainLog() { return brainRecord; },
    stats() { return { ...stats, score: computeScore(stats) }; },
    statsText() { return statsText(api.stats()); },
    setLocalClock(startHour) { localStartHour = startHour; },
    setRealism(level) {
      realism = realismOptions(level);
      // Re-derive per-type performance for everyone; instant compliance clears the queue.
      for (const ac of aircraft) { ac.perf = performanceFor(ac.type, realism.typeSpecificPerformance); assignCallsignForm(ac); }
      if (realism.complianceDelayS[1] === 0) for (const p of pending.splice(0)) if (!isRemovedPhase(p.ac.phase)) runTransmission(p.ac, p.instrs, true);
      const w = currentWind(); env.windDirDeg = w.directionDeg; env.windKt = w.speedKt;
      emit('event', { type: 'realism', level: realism.level });
    },
    setWind(wind, gusty = false) {
      // The runways in use stay as chosen at the start; only the flight model and reports change.
      baseWind = { directionDeg: wind.directionDeg, speedKt: Math.max(0, wind.speedKt) };
      gustyWind = gusty;
      const w = currentWind(); env.windDirDeg = w.directionDeg; env.windKt = w.speedKt;
    },
    on(event, handler) { (handlers[event] as Set<any>).add(handler); return () => (handlers[event] as Set<any>).delete(handler); },
    radioLog() { return radio; },
    addAircraft(spec) {
      let placed = spec;
      if (spec.kind === 'departure' && spec.queueIndex === undefined) {
        const runway = spec.runway ?? airport.departureRunway;
        const waiting = aircraft.filter((a) => a.kind === 'departure' && a.departureRunway?.name === runway && !isRemovedPhase(a.phase) && a.onGround);
        placed = { ...spec, queueIndex: waiting.length ? Math.max(...waiting.map((a) => Math.round(((a.holdShortPos ? distanceM(a.holdShortPos.lat, a.holdShortPos.lon, a.departureRunway!.lat, a.departureRunway!.lon) : 60) - (towerMode ? 110 : 60)) / 80))) + 1 : 0 };
      }
      const ac = createAircraft(airport, realism, placed, spawner.takeId()); spawn(ac); return ac;
    },
    declareEmergency(callsign, nature) {
      const ac = aircraft.find((a) => a.callsign === callsign && !isRemovedPhase(a.phase));
      if (!ac || ac.emergency) return false;
      beginEmergency(ac, nature);
      return true;
    },
    enableTrainingReadback() { trainingReadback = true; },
    academyCall(callsign, message, request, repeatAs) {
      const ac = callsign ? aircraft.find((a) => a.callsign === callsign && !isRemovedPhase(a.phase)) : undefined;
      if (callsign && !ac) return;
      if (ac && request) ac.pilot.request = newRequest(request, simTimeS);
      if (ac && repeatAs) academyRepeat.set(ac.id, repeatAs);
      say(ac ? 'pilot' : 'atc', ac ?? null, { text: message, speech: message }, ac ? 'report' : 'system');
      emit('event', { type: 'academyCall', callsign: callsign ?? '', text: message, ...(request ? { request } : {}) });
    },
    academyLaunchDeparture(callsign, initialAltitudeFt) {
      if (!options.academyApproach) return false;
      const ac = aircraft.find((a) => a.callsign === callsign && a.kind === 'departure' && isOnGroundPhase(a.phase));
      if (!ac) return false;
      runTransmission(ac, [{ kind: 'altitude', altitudeFt: initialAltitudeFt, expedite: false }, { kind: 'takeoff' }], false);
      return true;
    },
    academyTowerGoAround(callsign, reason) {
      if (!options.academyApproach) return false;
      const ac = aircraft.find((a) => a.callsign === callsign && !isRemovedPhase(a.phase));
      if (!ac || ac.kind !== 'arrival') return false;
      api.academyCall(null, `${airport.icao} tower: ${callsign} instructed to go around; ${reason}. Returning to approach.`);
      runTransmission(ac, [{ kind: 'abort' }], false);
      return true;
    },
    academyPrepareArrival(callsign, runway) {
      if (!options.academyApproach) return false;
      const ac = aircraft.find((a) => a.callsign === callsign && a.kind === 'arrival' && !isRemovedPhase(a.phase));
      if (!ac || !findRunwayEnd(airport, runway)) return false;
      runTransmission(ac, [{ kind: 'approach', runway }], false);
      return true;
    },
    setControllerVoiced(on) { controllerVoiced = on; },
    sayAgain(callsign, detail) {
      const ac = callsign ? aircraft.find((a) => a.callsign === callsign && !isRemovedPhase(a.phase)) : undefined;
      if (ac) say('pilot', ac, pilotPhrase({ text: detail === 'speed' ? 'say speed' : 'say again', speech: detail === 'speed' ? 'say speed' : 'say again' }, callsignPhrase(ac, tel())), 'report');
      else emit('message', 'Select an aircraft or say its callsign.');
    },
  };

  attachDebriefTimeline(api, timeline);

  if (options.initialTraffic !== false) {
    const counts = { arrivals: options.initialArrivals ?? 1, departures: options.initialDepartures ?? 1 };
    const spacingS = options.checkInSpacingS ?? DEFAULT_CHECKIN_SPACING_S;
    // Even the first call waits, so a host that opens paused (the ATIS brief) has a silent frequency
    // until the player takes it.
    spawner.initial(realism, aircraft, counts).forEach((ac, i) => spawn(ac, FIRST_CHECKIN_DELAY_S + i * spacingS));
  }
  if (towerMode) checkpoints.push(checkpoint());
  return api;
}
